<!--
  Angular 15: *ngIf / *ngFor only, no @if blocks.
-->
<div class="dl-page">

  <div class="dl-page__heading">
    <a class="dl-page__back" routerLink="/legal-documents" [queryParams]="document ? { platform: document.platform } : {}">
      <mat-icon>chevron_left</mat-icon>
      Legal documents
    </a>
  </div>

  <div class="dl-loading" *ngIf="loading">
    <div class="dl-spinner"></div>
    <p>Loading…</p>
  </div>

  <div class="dl-banner dl-banner--error" *ngIf="error">
    <mat-icon>error</mat-icon>
    <div class="dl-banner__body">{{ error }}</div>
  </div>

  <ng-container *ngIf="!loading && document as d">

    <div class="dl-page__header">
      <div class="dl-page__heading">
        <h1 class="dl-page__title">{{ d.title }}</h1>
        <p class="dl-page__subtitle legal-meta">
          <span class="legal-platform-tag" data-testid="legal-platform">{{ platformLabels[d.platform] }}</span>
          <span>{{ typeLabels[d.type] }}</span>
          <span *ngIf="d.version">version {{ d.version }}</span>
          <span [ngClass]="statusClass" data-testid="legal-status" [attr.data-status]="d.status">{{ statusLabel }}</span>
          <span *ngIf="d.publishedAt">published {{ d.publishedAt | date:'dd.MM.yyyy HH:mm' }}</span>
        </p>
      </div>
      <div class="dl-page__actions">
        <ng-container *ngIf="d.status === 'DRAFT'">
          <button type="button" class="dl-btn dl-btn--danger-ghost" (click)="askAction('delete')" data-testid="legal-delete">
            <mat-icon>delete</mat-icon>
            Delete draft
          </button>
          <button type="button" class="dl-btn" (click)="formOpen = true" data-testid="legal-edit">
            <mat-icon>edit</mat-icon>
            Edit
          </button>
          <button type="button" class="dl-btn dl-btn--primary" (click)="askAction('publish')" data-testid="legal-publish">
            <mat-icon>publish</mat-icon>
            Publish
          </button>
        </ng-container>
        <ng-container *ngIf="d.status === 'PUBLISHED'">
          <button type="button" class="dl-btn" (click)="exportCsv()" [disabled]="exporting" data-testid="legal-export">
            <mat-icon>download</mat-icon>
            {{ exporting ? 'Exporting…' : 'Export acceptances (CSV)' }}
          </button>
          <button type="button" class="dl-btn dl-btn--primary" *ngIf="d.current" (click)="newVersion()"
                  [disabled]="copying" data-testid="legal-new-version">
            <mat-icon>add</mat-icon>
            New version
          </button>
        </ng-container>
      </div>
    </div>

    <div class="dl-banner dl-banner--error" *ngIf="actionError">
      <mat-icon>error</mat-icon>
      <div class="dl-banner__body">{{ actionError }}</div>
    </div>

    <div class="dl-banner dl-banner--info" *ngIf="d.status === 'DRAFT'">
      <mat-icon>info</mat-icon>
      <div class="dl-banner__body">
        This is a draft. Users see nothing until it is published; then everyone has to accept it.
      </div>
    </div>

    <div class="dl-card" *ngIf="d.status === 'PUBLISHED'">
      <div class="dl-card__body">
        <div class="dl-kv">
          <div class="dl-kv__item">
            <span class="dl-kv__key">{{ d.type === 'DATA_PROCESSING_AGREEMENT' ? 'Organisations accepted' : 'Accepted' }}</span>
            <span class="dl-kv__value" data-testid="legal-accepted-count">{{ d.acceptedCount }}</span>
          </div>
          <div class="dl-kv__item" *ngIf="d.current">
            <span class="dl-kv__key">{{ d.type === 'DATA_PROCESSING_AGREEMENT' ? 'Organisations not yet' : 'Not yet accepted' }}</span>
            <span class="dl-kv__value" data-testid="legal-pending-count">{{ d.pendingCount ?? '—' }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="dl-card" *ngIf="d.changeSummary">
      <div class="dl-card__header">
        <h2 class="dl-card__title">What changed</h2>
      </div>
      <div class="dl-card__body legal-summary">{{ d.changeSummary }}</div>
    </div>

    <div class="dl-card">
      <div class="dl-card__header">
        <h2 class="dl-card__title">Text</h2>
      </div>
      <div class="dl-card__body legal-markdown legal-text" [innerHTML]="d.content | markdown"
           data-testid="legal-content"></div>
    </div>

    <!-- ── Who accepted ──────────────────────────────────────────────────── -->
    <div class="dl-card" *ngIf="d.status === 'PUBLISHED'">
      <mat-tab-group animationDuration="0ms" [(selectedIndex)]="tabIndex" data-testid="legal-acceptance-tabs">

        <mat-tab [label]="'Accepted (' + acceptancesTotal + ')'">
          <div class="dl-banner dl-banner--error" *ngIf="acceptancesError">
            <mat-icon>error</mat-icon>
            <div class="dl-banner__body">{{ acceptancesError }}</div>
          </div>
          <app-empty-state icon="hourglass_empty" title="Nobody has accepted yet"
                           *ngIf="!acceptancesLoading && !acceptancesError && acceptances.length === 0">
            Users accept the next time they open {{ platformLabels[d.platform] }}.
          </app-empty-state>
          <table class="dl-table dl-table--cards" dlCardTable *ngIf="acceptances.length > 0"
                 [class.dl-is-refreshing]="acceptancesLoading" data-testid="legal-acceptances-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Organization</th>
                <th>Accepted</th>
                <th>IP address</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let a of acceptances" [attr.data-testid]="'legal-acceptance-' + a.userId">
                <td class="dl-cell--title">{{ a.name || '(deleted account)' }}</td>
                <td>{{ a.email || '—' }}</td>
                <td>{{ a.tenantName || '—' }}</td>
                <td>{{ a.acceptedAt | date:'dd.MM.yyyy HH:mm:ss' }}</td>
                <td><span [matTooltip]="a.userAgent || ''">{{ a.ipAddress || '—' }}</span></td>
              </tr>
            </tbody>
          </table>
          <app-pagination *ngIf="acceptancesTotal > pageSize"
            [currentPage]="acceptancesPage"
            [pageSize]="pageSize"
            [totalElements]="acceptancesTotal"
            [totalPages]="totalPages(acceptancesTotal)"
            [loading]="acceptancesLoading"
            itemLabel="acceptances"
            (pageChange)="goToAcceptancesPage($event)">
          </app-pagination>
        </mat-tab>

        <!-- For a DPA: the owners of the organisations that have not accepted, one row per owner. -->
        <mat-tab *ngIf="d.current" [label]="(d.type === 'DATA_PROCESSING_AGREEMENT' ? 'Owners not yet accepted (' : 'Not yet accepted (') + pendingTotal + ')'">
          <div class="dl-banner dl-banner--error" *ngIf="pendingError">
            <mat-icon>error</mat-icon>
            <div class="dl-banner__body">{{ pendingError }}</div>
          </div>
          <app-empty-state icon="task_alt" title="Everyone has accepted"
                           *ngIf="!pendingLoading && !pendingError && pending.length === 0">
            Every {{ platformLabels[d.platform] }} user with an active membership has accepted this version.
          </app-empty-state>
          <table class="dl-table dl-table--cards" dlCardTable *ngIf="pending.length > 0"
                 [class.dl-is-refreshing]="pendingLoading" data-testid="legal-pending-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Organizations</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let p of pending" [attr.data-testid]="'legal-pending-' + p.userId">
                <td class="dl-cell--title">{{ p.name || '(unknown account)' }}</td>
                <td>{{ p.email || '—' }}</td>
                <td>{{ p.tenantNames.join(', ') || '—' }}</td>
              </tr>
            </tbody>
          </table>
          <app-pagination *ngIf="pendingTotal > pageSize"
            [currentPage]="pendingPage"
            [pageSize]="pageSize"
            [totalElements]="pendingTotal"
            [totalPages]="totalPages(pendingTotal)"
            [loading]="pendingLoading"
            itemLabel="users"
            (pageChange)="goToPendingPage($event)">
          </app-pagination>
        </mat-tab>
      </mat-tab-group>
    </div>
  </ng-container>
</div>

<app-legal-document-form-dialog
  [isOpen]="formOpen"
  [document]="document"
  (saved)="onSaved($event)"
  (cancel)="formOpen = false">
</app-legal-document-form-dialog>

<app-confirm-dialog
  [isOpen]="!!pendingAction"
  [title]="confirmTitle"
  [message]="confirmMessage"
  [confirmLabel]="pendingAction === 'publish' ? 'Publish' : 'Delete'"
  cancelLabel="Cancel"
  [confirmClass]="pendingAction === 'publish' ? 'dl-btn--primary' : 'dl-btn--danger'"
  [busy]="actionBusy"
  (confirm)="confirmAction()"
  (cancel)="pendingAction = null">
</app-confirm-dialog>
